import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const LICENSE_STORAGE_KEY = 'visionpos-license';

export interface LicenseData {
  licenseKey: string;
  licenseId: string;
  tenantId: string;
  deviceFingerprint: string;
  expiresAt: string;
  status: 'active' | 'suspended' | 'revoked';
  activatedAt: string;
}

export interface UseLicenseReturn {
  license: LicenseData | null;
  loading: boolean;
  isValid: boolean;
  activate: (key: string) => Promise<{ success: boolean; error?: string }>;
  clear: () => void;
}

/**
 * Génère une empreinte unique de l'appareil.
 * Sur Sunmi APK: numéro série + modèle injecté par le bridge Java.
 * Sur web: UUID persisté dans localStorage.
 */
export function getDeviceFingerprint(): string {
  const w = window as any;

  // 1) Bridge natif VisionPOS — numéro de série Sunmi
  if (w.__VISIONPOS_DEVICE_ID) return w.__VISIONPOS_DEVICE_ID;
  if (w.VisionPOSAndroid?.getDeviceId) {
    try {
      return w.VisionPOSAndroid.getDeviceId();
    } catch {}
  }

  // 2) Fallback: UUID persisté
  const FP_KEY = 'visionpos-device-fp';
  let fp = localStorage.getItem(FP_KEY);
  if (!fp) {
    fp = 'WEB-' + crypto.randomUUID();
    localStorage.setItem(FP_KEY, fp);
  }
  return fp;
}

function getDeviceName(): string {
  const w = window as any;
  if (w.__VISIONPOS_DEVICE_NAME) return w.__VISIONPOS_DEVICE_NAME;
  const ua = navigator.userAgent;
  if (ua.includes('SUNMI')) return 'Sunmi ' + (ua.match(/SUNMI\s*(\S+)/i)?.[1] || 'POS');
  if (/Android/i.test(ua)) return 'Android';
  if (/iPad|iPhone/i.test(ua)) return 'iOS';
  return navigator.platform || 'Navigateur web';
}

function getDeviceModel(): string {
  const w = window as any;
  if (w.__VISIONPOS_DEVICE_MODEL) return w.__VISIONPOS_DEVICE_MODEL;
  const ua = navigator.userAgent;
  if (ua.includes('SUNMI')) return ua.match(/SUNMI\s*\S+/i)?.[0] || 'SUNMI';
  return navigator.platform || 'Web';
}

function loadLocalLicense(): LicenseData | null {
  try {
    const raw = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LicenseData;
  } catch {
    return null;
  }
}

function saveLocalLicense(data: LicenseData) {
  localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(data));
}

/**
 * La licence est valide si elle a été activée (status === 'active').
 * Le check ne se fait qu'une seule fois à l'installation pour lier la machine.
 * Le super_admin peut suspendre/révoquer depuis le portail admin.
 */
function isLocallyValid(lic: LicenseData | null): boolean {
  if (!lic) return false;
  return lic.status === 'active';
}

export function useLicense(): UseLicenseReturn {
  const [license, setLicense] = useState<LicenseData | null>(loadLocalLicense);
  const [loading, setLoading] = useState(false);

  const isValid = isLocallyValid(license);

  // Activation unique — lie la licence à cette machine
  const activate = useCallback(
    async (key: string): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);
      try {
        const fp = getDeviceFingerprint();
        const name = getDeviceName();
        const model = getDeviceModel();

        const { data, error } = await supabase.rpc('activate_license', {
          p_license_key: key.toUpperCase().trim(),
          p_device_fingerprint: fp,
          p_device_name: name,
          p_device_model: model,
        });

        if (error) return { success: false, error: error.message };

        const result = data as any;
        if (!result?.success) return { success: false, error: result?.error || 'Erreur inconnue' };

        const licData: LicenseData = {
          licenseKey: key.toUpperCase().trim(),
          licenseId: result.license_id,
          tenantId: result.tenant_id,
          deviceFingerprint: fp,
          expiresAt: result.expires_at,
          status: 'active',
          activatedAt: new Date().toISOString(),
        };

        saveLocalLicense(licData);
        setLicense(licData);
        return { success: true };
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Erreur réseau';
        return { success: false, error: msg };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Clear licence (pour debug/reset ou suspension super_admin)
  const clear = useCallback(() => {
    localStorage.removeItem(LICENSE_STORAGE_KEY);
    setLicense(null);
  }, []);

  return { license, loading, isValid, activate, clear };
}
